import type { SrCustomerStatus } from '@plato/data/starrocks/types';
import { SrSalesOrderType } from '@plato/data/starrocks/types';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { v4 as uuidv4 } from 'uuid';
import { formatOrderId } from '../../../lib/order-helpers.ts';
import type { SearchEntityItemData } from '../../../server/types/search-types.ts';
import { GlobalSearchTab, SearchEntity } from '../../../server/types/search-types.ts';
import { api } from '../../../utils/api.ts';
import { useAppConfig } from '../../acl/acl-provider.tsx';
import { useAnalytics } from '../../analytics/hooks/use-analytics.ts';
import type { SearchBasePayload, SearchResultItem } from '../../analytics/types/analytics-events.ts';
import { useDrawers } from '../../drawers/all-drawers.tsx';

type SearchEntityKeys = keyof SearchEntityItemData;

export type SearchEntityItem<T extends SearchEntityKeys = SearchEntityKeys> = {
    entity: SearchEntity;
    id: string;
    name: string;
    subName?: string;
    extra?: string;
    status?: SrCustomerStatus;
    original: SearchEntityItemData[T];
};

export type SearchTracking = Pick<
    SearchBasePayload,
    'context' | 'customer_id' | 'context_entity_type' | 'context_entity_id'
>;

// result_id from the new Search API, or a constructed id for the legacy Manticore path
// (whose items carry no result_id).
const resolveResultId = (item: SearchEntityItem, requestId: string | undefined): string =>
    item.original.result_id ?? `${requestId}:${item.entity}:${item.id}`;

type UseSearchEntityDataProps = {
    entities: SearchEntity[];
    search: string;
    selectedTabKey: GlobalSearchTab;
    tracking: SearchTracking;
};

type UseSearchEntityDataReturn = {
    data: Map<SearchEntity, SearchEntityItem[]>;
    requestId: string | undefined;
    version: string | undefined;
    requestIds: Partial<Record<SearchEntity, string>> | undefined;
    dataUpdatedAt: number;
    limit: number;
    offset: number;
    isLoading: boolean;
    isFetched: boolean;
    trackResultClicked: (item: SearchEntityItem) => void;
};

export const getFilteredEntitiesByTab = (
    selectedTabKey: GlobalSearchTab,
    entities: SearchEntity[],
): SearchEntity[] => {
    switch (selectedTabKey) {
        case GlobalSearchTab.All:
            return entities;
        case GlobalSearchTab.Customers:
            return [SearchEntity.Customer];
        case GlobalSearchTab.Orders:
            return [SearchEntity.SalesOrder];
        case GlobalSearchTab.Quotes:
            return [SearchEntity.SalesQuote];
        case GlobalSearchTab.Products:
            return [SearchEntity.Product];
        default:
            return [];
    }
};

export const useSearchEntityData = ({
    entities,
    search,
    selectedTabKey,
    tracking,
}: UseSearchEntityDataProps): UseSearchEntityDataReturn => {
    const appConfig = useAppConfig();

    const filteredEntities: SearchEntity[] = getFilteredEntitiesByTab(selectedTabKey, entities);
    const limit = selectedTabKey === GlobalSearchTab.All ? 6 : 20;
    const offset = 0;

    const useNewSearchApi = appConfig.enableNewSearchApi;
    const queryInput = { search, limit, entities: filteredEntities };

    const legacyQuery = api.platoUser.search.search.useQuery(queryInput, {
        enabled: Boolean(search) && !useNewSearchApi,
    });
    const newApiQuery = api.platoUser.search.searchViaApi.useQuery(queryInput, {
        enabled: Boolean(search) && useNewSearchApi,
    });

    const activeQuery = useNewSearchApi ? newApiQuery : legacyQuery;
    const { data } = activeQuery;
    const isLoadingQuery = activeQuery.isLoading;
    const isFetchedQuery = activeQuery.isFetched;
    const dataUpdatedAt = activeQuery.dataUpdatedAt;

    const filteredItems = data?.items ?? [];

    const customers = filteredItems.filter(
        (item): item is SearchEntityItemData[SearchEntity.Customer] => item.entity === SearchEntity.Customer,
    );

    const orders = filteredItems.filter(
        (item): item is SearchEntityItemData[SearchEntity.SalesOrder] =>
            item.entity === SearchEntity.SalesOrder,
    );

    const quotes = filteredItems.filter(
        (item): item is SearchEntityItemData[SearchEntity.SalesQuote] =>
            item.entity === SearchEntity.SalesQuote,
    );

    const products = filteredItems.filter(
        (item): item is SearchEntityItemData[SearchEntity.Product] => item.entity === SearchEntity.Product,
    );

    const dataInOrder = new Map<SearchEntity, SearchEntityItem[]>();

    for (const entity of filteredEntities) {
        switch (entity) {
            case SearchEntity.Customer: {
                dataInOrder.set(
                    SearchEntity.Customer,
                    customers.map((customer) => ({
                        entity: SearchEntity.Customer,
                        id: customer.id,
                        name: customer.name,
                        subName:
                            [customer.city, customer.country_code].filter(Boolean).join(', ') || undefined,
                        extra: customer.erp_id,
                        status: customer.status,
                        original: customer,
                    })),
                );
                break;
            }
            case SearchEntity.Product: {
                dataInOrder.set(
                    SearchEntity.Product,
                    products.map((product) => ({
                        entity: SearchEntity.Product,
                        id: product.product_variant_id,
                        name: product.product_variant_name,
                        extra: product.product_variant_id,
                        original: product,
                    })),
                );

                break;
            }
            case SearchEntity.SalesOrder: {
                dataInOrder.set(
                    SearchEntity.SalesOrder,
                    orders.map((order) => ({
                        entity: SearchEntity.SalesOrder,
                        id: order.erp_id,
                        name: formatOrderId(appConfig, order.erp_id),
                        original: order,
                        extra: [order.customer_id, order.customer_name].filter(Boolean).join(' ') || '',
                    })),
                );
                break;
            }
            case SearchEntity.SalesQuote: {
                dataInOrder.set(
                    SearchEntity.SalesQuote,
                    quotes.map((quote) => ({
                        entity: SearchEntity.SalesQuote,
                        id: quote.erp_id,
                        name: formatOrderId(appConfig, quote.erp_id),
                        original: quote,
                        extra: [quote.customer_id, quote.customer_name].filter(Boolean).join(' ') || '',
                    })),
                );
                break;
            }
        }
    }

    const requestId = data?.request_id;
    const version = data?.version;
    const requestIds = data?.request_ids;

    const sendEvent = useAnalytics();

    const contextIdRef = useRef<string | undefined>(undefined);
    if (!contextIdRef.current) {
        contextIdRef.current = uuidv4();
    }
    const contextId = contextIdRef.current;

    const trackingContext = useMemo<SearchBasePayload>(
        () => ({
            context: tracking.context,
            context_id: contextId,
            customer_id: tracking.customer_id,
            context_entity_type: tracking.context_entity_type,
            context_entity_id: tracking.context_entity_id,
            target_entity_types: getFilteredEntitiesByTab(selectedTabKey, entities),
        }),
        [
            contextId,
            tracking.context,
            tracking.customer_id,
            tracking.context_entity_type,
            tracking.context_entity_id,
            entities,
            selectedTabKey,
        ],
    );

    useEffect(() => {
        sendEvent('search_opened', trackingContext);
    }, [sendEvent, trackingContext]);

    useEffect(() => {
        if (!dataUpdatedAt || !search) {
            return;
        }

        sendEvent('search_request', {
            ...trackingContext,
            request_id: requestId, // legacy Manticore envelope id; undefined for the new API
            version,
            request_ids: requestIds, // new API: per-entity ids (incl. empty calls); undefined for legacy
            search_query: search,
            limit,
            offset,
            results: [...dataInOrder.entries()].flatMap(([entity, items]) =>
                items.map<SearchResultItem>((item) => ({
                    entity_type: entity,
                    entity_id: item.id,
                    result_id: resolveResultId(item, requestId),
                    match_type: item.original.match_type,
                })),
            ),
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps -- fire once per unique response
    }, [sendEvent, dataUpdatedAt, trackingContext]);

    const trackResultClicked = useCallback(
        (item: SearchEntityItem) => {
            if (!dataUpdatedAt) {
                return;
            }

            sendEvent('search_result_clicked', {
                ...trackingContext,
                // New API: the clicked entity's request_id; legacy Manticore: the envelope id.
                request_id: requestIds?.[item.entity] ?? requestId,
                result_entity_type: item.entity,
                result_entity_id: item.id,
                result_id: resolveResultId(item, requestId),
                version,
                extra: {
                    match_type: item.original.match_type,
                },
            });
        },
        [sendEvent, dataUpdatedAt, trackingContext, requestId, requestIds, version],
    );

    return {
        data: dataInOrder,
        requestId,
        version,
        requestIds,
        dataUpdatedAt,
        limit,
        offset,
        isLoading: isLoadingQuery && Boolean(search),
        isFetched: isFetchedQuery,
        trackResultClicked,
    };
};

type SearchEntityNavigationItem = { erp_id: string; entity: SearchEntity };

type UseSearchEntityNavigationReturn = {
    navigateToEntity: (item: SearchEntityNavigationItem) => void;
};

export const useSearchEntityNavigation = (): UseSearchEntityNavigationReturn => {
    const navigate = useNavigate();
    const { openProductDetails, openOrderQuoteDetails } = useDrawers();

    const navigateToEntity = useCallback(
        (item: SearchEntityNavigationItem) => {
            const { entity, erp_id } = item;

            switch (entity) {
                case SearchEntity.Customer:
                    void navigate(`/customers/${erp_id}`);
                    break;
                case SearchEntity.Product:
                    openProductDetails(erp_id);
                    break;
                case SearchEntity.SalesOrder:
                case SearchEntity.SalesQuote:
                    openOrderQuoteDetails(
                        entity === SearchEntity.SalesOrder ? SrSalesOrderType.ORDER : SrSalesOrderType.QUOTE,
                        erp_id,
                    );
                    break;
            }
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [],
    );

    return {
        navigateToEntity,
    };
};
